// src/App.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabaseClient";
import Home from "./pages/Home";
import Stats from "./pages/Stats";
import Settings from "./pages/Settings";
import { ui, cx } from "./ui/ui";

function normalize(email) {
  return String(email || "").trim().toLowerCase();
}

export default function App() {
  const allowed = useMemo(() => {
    const a = normalize(import.meta.env.VITE_ALLOWED_EMAIL_1);
    const b = normalize(import.meta.env.VITE_ALLOWED_EMAIL_2);
    return new Set([a, b].filter(Boolean));
  }, []);

  const [session, setSession] = useState(null);

  // Auth UI state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session || null);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession || null);
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const signedInEmail = normalize(session?.user?.email);
  const isAllowed = session?.user?.email ? allowed.has(signedInEmail) : false;

  async function signInWithPassword() {
    setStatus("");
    const e = normalize(email);

    if (!e) return setStatus("Enter an email.");
    if (!allowed.has(e)) return setStatus("That email is not allowed for this app.");
    if (!password) return setStatus("Enter your password.");

    const { error } = await supabase.auth.signInWithPassword({ email: e, password });
    if (error) return setStatus(error.message || "Failed to sign in.");

    setPassword("");
    setStatus("");
  }

  async function signOut() {
    await supabase.auth.signOut();
    setSession(null);
    setEmail("");
    setPassword("");
    setStatus("");
  }

  if (loading) {
    return (
      <div className={ui.center}>
        <div className={cx(ui.card, "w-full max-w-md")}>
          <div className={ui.cardPad}>
            <div className={ui.h1}>Loading…</div>
            <div className={ui.muted}>Warming up the otters.</div>
          </div>
        </div>
      </div>
    );
  }

  // Signed in but not allowlisted
  if (session && !isAllowed) {
    return (
      <div className={ui.center}>
        <div className={cx(ui.card, "w-full max-w-md")}>
          <div className={ui.cardPad}>
            <div className={ui.h1}>Access denied</div>
            <div className={cx(ui.muted, "mt-2")}>Signed in but not allowlisted in the app.</div>

            <div className="mt-3 text-sm text-white/85">
              Signed in as: <b>{session.user.email}</b>
            </div>

            <button className={cx(ui.btn, ui.btnGhost, "mt-4 w-full")} onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Not signed in -> pretty login
  if (!session) {
    return (
      <div className={ui.center}>
        <div className={cx(ui.card, "w-full max-w-md")}>
          <div className={ui.cardPad}>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl border border-line bg-white/5 grid place-items-center">
                <span className="text-lg">🦦</span>
              </div>
              <div>
                <div className={ui.h1}>OtterLink</div>
                <div className={ui.muted}>Private couple pings. Sign in.</div>
              </div>
            </div>

            <div className="mt-5 grid gap-3">
              <label className="grid gap-2">
                <span className={ui.label}>Email</span>
                <input
                  className={ui.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </label>

              <label className="grid gap-2">
                <span className={ui.label}>Password</span>
                <input
                  className={ui.input}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  type="password"
                  autoComplete="current-password"
                />
              </label>

              <button className={cx(ui.btn, ui.btnPrimary, "mt-1")} onClick={signInWithPassword}>
                Sign in
              </button>

              {status ? <div className="text-sm text-white/80">{status}</div> : null}

              <div className="text-xs text-white/60">
                Tip: once signed in, the installed app stays signed in.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Signed in and allowed
  return <AuthedApp onSignOut={signOut} />;
}

function AuthedApp({ onSignOut }) {
  const tabs = useMemo(
    () => [
      { key: "home", label: "Home" },
      { key: "stats", label: "Stats" },
      { key: "settings", label: "Settings" },
    ],
    []
  );

  const [tab, setTab] = useState("home");

  return (
    <div className={ui.page}>
      <div className="sticky top-0 z-10 border-b border-line bg-black/40 backdrop-blur">
        <div className="mx-auto flex max-w-xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-2xl border border-line bg-white/5 grid place-items-center">
              <span className="text-base">🦦</span>
            </div>
            <div className="font-black tracking-tight">OtterLink</div>
          </div>

          <div className="flex gap-2">
            {tabs.map((t) => (
              <button
                key={t.key}
                className={
                  "rounded-full px-3 py-1 text-xs font-black border " +
                  (tab === t.key
                    ? "border-white/30 bg-white/10"
                    : "border-line2 bg-transparent text-white/80 hover:bg-white/5")
                }
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
            <button
              className="rounded-full px-3 py-1 text-xs font-black border border-line2 text-white/80 hover:bg-white/5"
              onClick={onSignOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className={ui.container}>
        {tab === "home" ? <Home /> : null}
        {tab === "stats" ? <Stats /> : null}
        {tab === "settings" ? <Settings /> : null}
      </div>
    </div>
  );
}
